import { type RefObject, useCallback, useEffect, useRef } from 'react';

import type { ICard, TCardId } from '../../types';

import type { TCardHeightCalculationState } from './CardHeightCalculationStateManager';

const chunkSize = 300;

const requestIdleCallbackPolyfill =
  window?.requestIdleCallback ||
  ((cb: (deadline: IdleDeadline) => void): number =>
    setTimeout(() => cb({ timeRemaining: () => 50, didTimeout: false }), 1) as unknown as number);

const cancelIdleCallbackPolyfill = window?.cancelIdleCallback || clearTimeout;

const getFirstNSetItems = <T>(set: Set<T>, n: number): T[] => {
  const result: T[] = [];
  const iterator = set.values();
  let count = 0;

  while (count < n) {
    const { value, done } = iterator.next();
    if (done) break;
    result.push(value);
    count++;
  }

  return result;
};

type TUseCardHeightCalculationProps = {
  calculateCardHeightRef: RefObject<TCardHeightCalculationState['calculateCardHeight'] | undefined>;
  onCardsHeightChange: (changes: Map<TCardId, number>) => void;
  unmeasuredCards: Set<TCardId>;
  cardProps: Map<TCardId, ICard>;
};

// COULDDO: Use web workers instead of requestIdleCallback to calculate card heights at once
export const useCardsHeightCalculation = ({
  calculateCardHeightRef,
  onCardsHeightChange,
  unmeasuredCards,
  cardProps,
}: TUseCardHeightCalculationProps) => {
  const unmeasuredCardsRef = useRef(new Set(unmeasuredCards));
  const cardPropsRef = useRef(cardProps);
  const scheduledCalculationId = useRef<number | null>(null);
  const isCalculating = useRef(false);

  useEffect(() => {
    cardPropsRef.current = cardProps;
  }, [cardProps]);

  const cancelScheduledCalculation = useCallback(() => {
    if (scheduledCalculationId.current !== null) {
      cancelIdleCallbackPolyfill(scheduledCalculationId.current);
      scheduledCalculationId.current = null;
    }
  }, []);

  const calculateNextChunk = useCallback(
    (_deadline: IdleDeadline) => {
      if (!isCalculating.current) return;

      const cardsToCalculate = getFirstNSetItems(unmeasuredCardsRef.current, chunkSize);
      const changes = new Map<TCardId, number>();

      cardsToCalculate.forEach((cardId) => {
        const props = cardPropsRef.current.get(cardId);
        if (props && calculateCardHeightRef?.current) {
          unmeasuredCardsRef.current.delete(cardId);
          changes.set(cardId, calculateCardHeightRef.current(props));
        }
      });

      onCardsHeightChange(changes);

      if (unmeasuredCardsRef.current.size > 0) {
        scheduledCalculationId.current = requestIdleCallbackPolyfill(calculateNextChunk);
      } else {
        isCalculating.current = false;
        scheduledCalculationId.current = null;
      }
    },
    [calculateCardHeightRef, onCardsHeightChange],
  );

  const scheduleCalculation = useCallback(() => {
    if (!unmeasuredCardsRef.current.size) {
      isCalculating.current = false;
      return;
    }

    if (!isCalculating.current) {
      isCalculating.current = true;
      scheduledCalculationId.current = requestIdleCallbackPolyfill(calculateNextChunk);
    }
  }, [calculateNextChunk]);

  useEffect(() => {
    unmeasuredCardsRef.current = new Set(unmeasuredCards);

    if (unmeasuredCardsRef.current.size > 0) {
      scheduleCalculation();
    }

    return () => {
      if (!isCalculating.current) cancelScheduledCalculation();
    };
  }, [unmeasuredCards, scheduleCalculation, cancelScheduledCalculation]);
};
